import { OrderStatus, PaymentStatus, ShippingType } from '@services/orders/interfaces/orders.interfaces';

/**
 * Clave de la pastilla activa. No es el estado: 'pending' se abre en dos pastillas
 * (envío y retiro), que en la base son el mismo estado con distinto shippingType.
 */
export type OrderStatusFilter = OrderStatus | 'ALL' | 'pending-delivery' | 'pending-pickup' | 'payment-unconfirmed';

export interface OrderStatusFilterOption {
  value: OrderStatusFilter;
  label: string;
  /** Filtros que se mandan al back; ausente en 'Todos'. */
  status?: OrderStatus;
  shippingType?: ShippingType;
  paymentStatus?: PaymentStatus;
}

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  pending: 'Pendiente por enviar',
  shipped: 'Enviado',
  completed: 'Completado',
  cancelled: 'Cancelado',
  returned: 'Devuelto',
  // Set by the backend when the payment is rejected or cancelled; the admin can't pick it by hand.
  payment_failed: 'Error con el pago',
};

/** Etiqueta de "pendiente" para retiro en tienda: no hay envío, así que no aplica "por enviar". */
const PICKUP_PENDING_LABEL = 'Pendiente por retirar';

/** Igual que ORDER_STATUS_LABELS, salvo "pendiente" en pedidos de retiro (no de envío a domicilio). */
export function statusLabelFor(status: OrderStatus, shippingType: ShippingType): string {
  if (status === 'pending' && shippingType === 'pickup') {
    return PICKUP_PENDING_LABEL;
  }
  return ORDER_STATUS_LABELS[status];
}

export const SHIPPING_TYPE_LABELS: Record<ShippingType, string> = {
  delivery: 'A domicilio',
  pickup: 'Retiro',
};

/** Statuses shown as filter pills sobre el listado (el dropdown por fila usa selectableStatusesFor). */
export const ORDER_STATUSES: readonly OrderStatus[] = [
  'pending',
  'shipped',
  'completed',
  'cancelled',
  'returned',
  'payment_failed',
];

/**
 * Estados a los que se puede pasar desde cada estado actual (pedidos a domicilio). Debe reflejar
 * ORDER_STATUS_TRANSITIONS del back (src/constants/global.constants.ts): pendiente → enviado
 * o cancelado; enviado → completado o cancelado; completado → devuelto; cancelado, devuelto y
 * error con el pago son finales. "payment_failed" nunca aparece como destino: solo lo asigna
 * el back automáticamente, no es una transición manual del admin.
 */
export const ORDER_STATUS_TRANSITIONS: Record<OrderStatus, readonly OrderStatus[]> = {
  pending: ['shipped', 'cancelled'],
  shipped: ['completed', 'cancelled'],
  completed: ['returned'],
  cancelled: [],
  returned: [],
  payment_failed: [],
};

// Retiro en tienda no pasa por "enviado" (no hay despacho de Chilexpress): de pendiente va
// directo a completado (cuando el cliente retira) o cancelado. Debe reflejar
// PICKUP_PENDING_TRANSITIONS del back.
const PICKUP_PENDING_TRANSITIONS: readonly OrderStatus[] = ['completed', 'cancelled'];

function transitionsFor(status: OrderStatus, shippingType: ShippingType): readonly OrderStatus[] {
  if (status === 'pending' && shippingType === 'pickup') {
    return PICKUP_PENDING_TRANSITIONS;
  }
  return ORDER_STATUS_TRANSITIONS[status];
}

/** Opciones válidas para el dropdown de un pedido: su estado actual más los estados a los que puede pasar. */
export function selectableStatusesFor(status: OrderStatus, shippingType: ShippingType): OrderStatus[] {
  return [status, ...transitionsFor(status, shippingType)];
}

/** true si el estado no tiene ninguna transición posible (cancelado, devuelto, o pendiente-retiro ya resuelto): el select debe quedar bloqueado. */
export function isFinalStatus(status: OrderStatus, shippingType: ShippingType): boolean {
  return transitionsFor(status, shippingType).length === 0;
}

export const ORDER_STATUS_FILTERS: readonly OrderStatusFilterOption[] = [
  { value: 'ALL', label: 'Todos' },
  // Los pendientes van separados por tipo de envío: son trabajos distintos
  // (despachar vs. tener listo para el retiro) aunque compartan estado.
  { value: 'pending-delivery', label: ORDER_STATUS_LABELS.pending, status: 'pending', shippingType: 'delivery' },
  { value: 'pending-pickup', label: PICKUP_PENDING_LABEL, status: 'pending', shippingType: 'pickup' },
  ...ORDER_STATUSES.filter((status) => status !== 'pending').map((status) => ({
    value: status as OrderStatusFilter,
    label: ORDER_STATUS_LABELS[status],
    status,
  })),
  { value: 'payment-unconfirmed', label: 'Pago sin confirmar', paymentStatus: 'unconfirmed' },
];

export const UNRESOLVED_PAYMENT_LABELS: Record<Exclude<PaymentStatus, 'paid'>, string> = {
  pending: 'Pago en proceso',
  unconfirmed: 'Pago sin confirmar',
  rejected: 'Pago rechazado',
};

/**
 * Motivos preseleccionables al cancelar un pedido. Son oraciones ya redactadas (no códigos):
 * el back guarda tal cual lo que llega en `reason`, así que el texto elegido (o el que el
 * administrador escriba en "Otro") queda como el motivo real, legible por el cliente o soporte.
 */
export const CANCEL_ORDER_REASONS: readonly string[] = [
  'El cliente se arrepintió de la compra.',
  'El cliente pidió un cambio que no pudimos realizar (diseño, color, tamaño, etc.).',
  'No fue posible fabricar el producto (sin stock de un insumo).',
  'Había un error en los datos del pedido (dirección, talla, color, cantidad, etc.).',
  'Pedido duplicado.',
];

/** Motivos sugeridos al marcar un pedido como devuelto; registrarlo es opcional. */
export const RETURN_ORDER_REASONS: readonly string[] = [
  'El producto llegó dañado o con fallas.',
  'El producto no corresponde a lo pedido (diseño, color, tamaño, etc.).',
  'El cliente se arrepintió después de recibirlo.',
  'El pedido nunca llegó a destino y volvió.',
];

export const ORDER_DATE_FORMAT = 'dd/MM/yyyy';

/** Items listed in the "Pedido" column before collapsing into "+N más". */
export const ORDER_SUMMARY_VISIBLE_ITEMS = 2;

/** Página pública de seguimiento; el número de orden de transporte va al final. */
export const CHILEXPRESS_TRACKING_URL = 'https://centrodeayuda.chilexpress.cl/seguimiento/';

/** Same limit as the backend column: about 39 transport order numbers of 12 digits. */
export const CHILEXPRESS_TRACKING_CODE_MAX_LENGTH = 512;
/** One or more Chilexpress transport order numbers (one per parcel), comma separated. Same format the backend validates. */
export const CHILEXPRESS_TRACKING_CODE_PATTERN = /^\d{8,20}(\s*,\s*\d{8,20})*$/;
export const CHILEXPRESS_TRACKING_CODE_SEPARATOR = /\s*,\s*/;
